import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  useTheme,
  CircularProgress,
  TablePagination,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Grid,
  TableSortLabel,
  javascriptimport,
  Collapse,
} from "@mui/material";
import GetAppIcon from "@mui/icons-material/GetApp";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import DescriptionIcon from "@mui/icons-material/Description";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import TableViewIcon from "@mui/icons-material/TableView";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import ClearAllIcon from "@mui/icons-material/ClearAll";
//for pdf Export
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
//for excel Export
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import axios from "../../utils/axiosInstance";
import { useNavigate } from "react-router-dom";
import InfoNotification from "./InfoNotification";
import { getStateDescriptions } from "../../utils/things/PRECISION";
import { formatReadableDate } from "../../utils/formatDate";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

export default function CurrentAlarms() {
  const theme = useTheme();
  const navigate = useNavigate();

  // expand state
  const [expandedAlarmsRows, setExpandedAlarmsRows] = useState({});
  const [expandedNotificationsRows, setExpandedNotificationsRows] = useState(
    {},
  );

  // Alarms state
  const [alarms, setAlarms] = useState([]);
  const [filteredAlarms, setFilteredAlarms] = useState([]);
  const [alarmsQuery, setAlarmsQuery] = useState("");
  const [loadingAlarms, setLoadingAlarms] = useState(false);

  // Notifications state
  const [notifications, setNotifications] = useState([]);
  const [filteredNotifications, setFilteredNotifications] = useState([]);
  const [notificationsQuery, setNotificationsQuery] = useState("");
  const [loadingNotifications, setLoadingNotifications] = useState(false);

  // Pagination states for alarms
  const [alarmsPage, setAlarmsPage] = useState(0);
  const [alarmsRowsPerPage, setAlarmsRowsPerPage] = useState(10);

  // Pagination states for notifications
  const [notificationsPage, setNotificationsPage] = useState(0);
  const [notificationsRowsPerPage, setNotificationsRowsPerPage] = useState(10);

  // Export menu state
  const [alarmExportMenuAnchor, setAlarmExportMenuAnchor] = useState(null);
  const [notificationsExportMenuAnchor, setNotificationsExportMenuAnchor] =
    useState(null);

  const [showInformationNotification, setShowInformationNotification] =
    useState(false);

  const [selectedNotification, setSelectedNotification] = useState(null);

  // Sorting states
  const [alarmsSortConfig, setAlarmsSortConfig] = useState({
    key: "time",
    direction: "desc",
  });
  const [notificationsSortConfig, setNotificationsSortConfig] = useState({
    key: null,
    direction: "asc",
  });

  const [filteredAreas, setFilteredAreas] = useState([]);
  const [notificationFilter, setNotificationFilter] = useState("criticalAlarm");

  const handleAlarmsSort = (key) => {
    // let direction = "desc";
    // if (alarmsSortConfig.key === key && alarmsSortConfig.direction === "asc") {
    //   direction = "desc";
    // }

    // setAlarmsSortConfig({ key, direction });

    // Apply sorting logic to your filtered alarms
    // const sorted = [...filteredAlarms].sort((a, b) => {
    //   return direction === "asc"
    //     ? Number(a[key]) - Number(b[key])
    //     : Number(b[key]) - Number(a[key]);
    // });

    // setFilteredAlarms(sorted);

    setAlarmsSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const handleNotificationsSort = (key) => {
    let direction = "asc";
    if (
      notificationsSortConfig.key === key &&
      notificationsSortConfig.direction === "asc"
    ) {
      direction = "desc";
    }

    setNotificationsSortConfig({ key, direction });

    // Apply sorting logic to your filtered notifications
    const sorted = [...filteredNotifications].sort((a, b) => {
      if (a[key] < b[key]) return direction === "asc" ? -1 : 1;
      if (a[key] > b[key]) return direction === "asc" ? 1 : -1;
      return 0;
    });

    setFilteredNotifications(sorted);
  };

  const OpenInfo = (notificationData) => {
    setSelectedNotification(notificationData);
    setShowInformationNotification(true);
  };

  const handleAlarmExportMenuClose = () => {
    setAlarmExportMenuAnchor(null);
  };

  const handleNotificationsExportMenuClose = () => {
    setNotificationsExportMenuAnchor(null);
  };

  const toggleAlarmsRowExpansion = (rowId) => {
    setExpandedAlarmsRows((prev) => ({ ...prev, [rowId]: !prev[rowId] }));
  };

  const toggleNotificationsRowExpansion = (rowId) => {
    setExpandedNotificationsRows((prev) => ({
      ...prev,
      [rowId]: !prev[rowId],
    }));
  };

  // ===== ALARMS EXPORT FUNCTIONS =====

  const copyToClipboard = () => {
    const textContent = filteredAlarms
      .map((alarm) => {
        return `${new Date(alarm.time)}\t${alarm.unitName}\t${alarm.unitId}\t${alarm.regionTechnician
          }\t${alarm.shutdown}`;
      })
      .join("\n");

    const headers =
      "Alarm Time\tUnit Name\tModem Serial Number\tRegion/Technician\tShutdown Enum";

    navigator.clipboard
      .writeText(`${headers}\n${textContent}`)
      .then(() => console.log(`Current Alarms data copied to clipboard`))
      .catch((err) => console.error("Failed to copy:", err));

    handleAlarmExportMenuClose();
    toast.success("Copied current Alarms Data to clipboard");
  };

  const exportToCSV = () => {
    const csvContent = [
      [
        "Alarm Time",
        "Unit Name",
        "Modem Serial Number",
        "Region/Technician",
        "Shutdown Enum",
      ],
      ...filteredAlarms.map((alarm) => [
        new Date(alarm.time),
        alarm.unitName,
        alarm.unitId,
        alarm.regionTechnician,
        alarm.shutdown,
      ]),
    ];

    const csvString = csvContent
      .map((row) => row.map((field) => `"${field}"`).join(","))
      .join("\n");

    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `Current_Alarms_${new Date().toISOString().split("T")[0]
      }.csv`;
    link.click();

    handleAlarmExportMenuClose();
    toast.success("Exported Current Alarms to CSV");
  };

  const exportToExcel = () => {
    const headers = [
      "Alarm Time",
      "Unit Name",
      "Modem Serial Number",
      "Region/Technician",
      "Shutdown Enum",
    ];

    const rows = filteredAlarms.map((alarm) => [
      new Date(alarm.time),
      alarm.unitName,
      alarm.unitId,
      alarm.regionTechnician,
      alarm.shutdown,
    ]);

    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);

    // ✅ Set date-time format for column A (Alarm Time)
    const range = XLSX.utils.decode_range(worksheet["!ref"]);
    for (let R = 1; R <= range.e.r; ++R) {
      const cellAddress = XLSX.utils.encode_cell({ r: R, c: 0 }); // column A
      if (worksheet[cellAddress]) {
        worksheet[cellAddress].z = "yyyy-mm-dd hh:mm:ss";
      }
    }

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Current Alarms");

    XLSX.writeFile(
      workbook,
      `Current_Alarms_${new Date().toISOString().split("T")[0]}.xlsx`,
    );

    handleAlarmExportMenuClose();
    toast.success(`Exported Current Alarms to Excel`);
  };

  const exportToPDF = () => {
    const doc = new jsPDF("l", "pt", "a4"); // landscape, points, A4

    // Title
    doc.setFontSize(12);
    doc.text("Current Alarms Report", 40, 30);

    // Table headers and rows
    const headers = [
      [
        "Alarm Time",
        "Unit Name",
        "Modem Serial Number",
        "Region/Technician",
        "Shutdown Enum",
      ],
    ];

    const rows = filteredAlarms.map((alarm) => [
      new Date(alarm.time),
      alarm.unitName,
      alarm.unitId,
      alarm.regionTechnician,
      alarm.shutdown,
    ]);

    autoTable(doc, {
      head: headers,
      body: rows,
      startY: 50,
      theme: "grid",
      styles: {
        fontSize: 8,
        cellPadding: 2,
        overflow: "linebreak",
      },
      headStyles: { fillColor: [211, 47, 47] }, // Red header color
      tableWidth: "auto",
      columnStyles: {
        0: { cellWidth: 120 }, // Alarm Time
        1: { cellWidth: 250 }, // Unit Name
        2: { cellWidth: 120 }, // Region/Technician
        3: { cellWidth: 180 }, // Shutdown Enum
      },
    });

    doc.save(`Current_Alarms_${new Date().toISOString().split("T")[0]}.pdf`);
    handleAlarmExportMenuClose();
    toast.success(`Exported Current Alarms to PDF`);
  };
  // ===== ALARMS EXPORT FUNCTIONS =====
  // ===== NOTIFICATIONS EXPORT FUNCTIONS =====

  const copyNotificationsToClipboard = () => {
    const textContent = filteredNotifications
      .map((notif) => {
        return `${new Date(notif.time)}\t${notif.notification}\t${notif.recipientsLength + "/" + notif.emailsLength
          }`;
      })
      .join("\n");

    const headers = "Time\tNotification\tRecipients";

    navigator.clipboard
      .writeText(`${headers}\n${textContent}`)
      .then(() => console.log(`Notifications data copied to clipboard`))
      .catch((err) => console.error("Failed to copy:", err));

    handleNotificationsExportMenuClose();
    toast.success("Copied notification logs to clipboard");
  };

  const exportNotificationsToCSV = () => {
    const csvContent = [
      ["Time", "Notification", "Recipients"],
      ...filteredNotifications.map((notif) => [
        new Date(notif.time),
        notif.notification,
        notif.recipientsLength + "/" + notif.emailsLength,
      ]),
    ];

    const csvString = csvContent
      .map((row) => row.map((field) => `"${field}"`).join(","))
      .join("\n");

    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `Notifications_Log_${new Date().toISOString().split("T")[0]
      }.csv`;
    link.click();

    handleNotificationsExportMenuClose();
    toast.success(`Exported Notifications to CSV`);
  };

  const exportNotificationsToExcel = () => {
    const headers = ["Time", "Notification", "Recipients"];

    const rows = filteredNotifications.map((notif) => [
      new Date(notif.time),
      notif.notification,
      notif.recipientsLength + "/" + notif.emailsLength,
    ]);

    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);

    // ✅ Force date-time format for column A (Time)
    const range = XLSX.utils.decode_range(worksheet["!ref"]);
    for (let R = 1; R <= range.e.r; ++R) {
      const cellAddress = XLSX.utils.encode_cell({ r: R, c: 0 }); // column A
      if (worksheet[cellAddress]) {
        worksheet[cellAddress].z = "yyyy-mm-dd hh:mm:ss";
      }
    }

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Notifications");

    XLSX.writeFile(
      workbook,
      `Notifications_Log_${new Date().toISOString().split("T")[0]}.xlsx`,
    );

    handleNotificationsExportMenuClose();
    toast.success(`Exported Notifications to Excel`);
  };

  const exportNotificationsToPDF = () => {
    const doc = new jsPDF("l", "pt", "a4");

    doc.setFontSize(12);
    doc.text("Notifications Log Report", 40, 30);

    const headers = [["Time", "Notification", "Recipients"]];

    const rows = filteredNotifications.map((notif) => [
      new Date(notif.time),
      notif.notification,
      notif.recipientsLength + "/" + notif.emailsLength,
    ]);

    autoTable(doc, {
      head: headers,
      body: rows,
      startY: 50,
      theme: "grid",
      styles: {
        fontSize: 8,
        cellPadding: 3,
        overflow: "linebreak",
      },
      headStyles: { fillColor: [211, 47, 47] }, // Red header color
      tableWidth: "auto",
      columnStyles: {
        0: { cellWidth: 120 }, // Time
        1: { cellWidth: 350 }, // Notification
        2: { cellWidth: 200 }, // Recipients
      },
    });

    doc.save(`Notifications_Log_${new Date().toISOString().split("T")[0]}.pdf`);
    handleNotificationsExportMenuClose();
    toast.success(`Exported Notifications to PDF`);
  };
  // ===== NOTIFICATIONS EXPORT FUNCTIONS =====

  // Fetch alarms data
  const fetchAlarms = async () => {
    try {
      setLoadingAlarms(true);
      // Replace with your actual API call
      const response = await axios.get(
        `${process.env.REACT_APP_API_GATEAWAY}/iot/alarms/currentAlarms`,
      );
      const events = await axios.get(
        `${process.env.REACT_APP_API_GATEAWAY}/eventParties`,
        {
          params: { companyId: process.env.REACT_APP_COMPANY_ID },
        }
      );
      const sdsData = events.data;
      const alarms = response.data
        .filter((alarm) => !(alarm.state === 0 && alarm.shutdownEnum === 0))
        .map((alarm) => {
          if (
            alarm.controllerType == "WellPro II" &&
            alarm.state == 12 &&
            alarm.shutdownEnum == 0
          ) {
            return {
              ...alarm,
              shutdown: "Engine Stall",
            };
          }

          const { stateDescr, shutdownDescr } = getStateDescriptions(
            alarm.controllerType,
            alarm.state,
            alarm.shutdownEnum, sdsData
          );
          return {
            ...alarm,
            shutdown: shutdownDescr,
          };
        });
      // let direction = "asc";
      // // Apply sorting logic to your filtered alarms
      // const sorted = [...alarms].sort((a, b) => {
      //   if (a["time"] < b["time"]) return direction === "asc" ? -1 : 1;
      //   if (a["time"] > b["time"]) return direction === "asc" ? 1 : -1;
      //   return 0;
      // });
      setAlarms(alarms);
      setFilteredAlarms(alarms);
      setLoadingAlarms(false);
      //  console.log(alarms);
    } catch (err) {
      console.error("Error fetching alarms:", err);
      setLoadingAlarms(false);
    }
  };

  // Fetch Notifications data
  const fetchNotifications = async () => {
    try {
      setLoadingNotifications(true);
      // Replace with your actual API call
      const response = await axios.get(
        `${process.env.REACT_APP_API_GATEAWAY}/iot/alarms/notificationLogs`,
      );
      setNotifications(response.data);
      setFilteredNotifications(response.data);
      setLoadingNotifications(false);
    } catch (err) {
      console.error("Error fetching notifications:", err);
      setLoadingNotifications(false);
    }
  };

  // Fetch alarm logs data
  const fetchAlarmLogs = async () => {
    try {
      setLoadingNotifications(true);
      // Replace with your actual API call
      const response = await axios.get(
        `${process.env.REACT_APP_API_GATEAWAY}/iot/alarms/alarmLogs`,
      );

      setNotifications(response.data);
      setFilteredNotifications(response.data);
      setLoadingNotifications(false);
    } catch (err) {
      console.error("Error fetching notifications:", err);
      setLoadingNotifications(false);
    }
  };

  // Fetch notifications data
  const fetchClearLogs = async () => {
    try {
      setLoadingNotifications(true);
      // Replace with your actual API call
      const response = await axios.get(
        `${process.env.REACT_APP_API_GATEAWAY}/iot/alarms/clearLogs`,
      );

      setNotifications(response.data);
      setFilteredNotifications(response.data);
      setLoadingNotifications(false);
    } catch (err) {
      console.error("Error fetching notifications:", err);
      setLoadingNotifications(false);
    }
  };

  useEffect(() => {
    fetchAlarms();
    fetchAlarmLogs();
  }, []);

  // Search handlers
  const handleAlarmsSearch = (e) => {
    const q = e.target.value.toLowerCase();
    setAlarmsQuery(e.target.value);
    const filtered = alarms.filter((alarm) => {
      return (
        (alarm?.unitName || "").toLowerCase().includes(q) ||
        (alarm?.unitId || "").toLowerCase().includes(q) ||
        (alarm?.regionTechnician || "").toLowerCase().includes(q) ||
        (alarm?.shutdown || "").toLowerCase().includes(q) ||
        (alarm?.time || "").toLowerCase().includes(q)
      );
    });
    setFilteredAlarms(filtered);
    setAlarmsPage(0);
  };

  const handleNotificationsSearch = (e) => {
    const q = e.target.value.toLowerCase();
    setNotificationsQuery(e.target.value);
    const filtered = notifications.filter((notif) => {
      return (
        (notif?.notification || "").toLowerCase().includes(q) ||
        (notif?.recipients || "").toLowerCase().includes(q) ||
        (new Date(notif?.time).toLocaleString() || "").toLowerCase().includes(q)
      );
    });
    setFilteredNotifications(filtered);
    setNotificationsPage(0);
  };

  // Pagination handlers
  const handleAlarmsChangePage = (event, newPage) => {
    setAlarmsPage(newPage);
  };

  const handleAlarmsChangeRowsPerPage = (event) => {
    setAlarmsRowsPerPage(parseInt(event.target.value, 10));
    setAlarmsPage(0);
  };

  const handleNotificationsChangePage = (event, newPage) => {
    setNotificationsPage(newPage);
  };

  const handleNotificationsChangeRowsPerPage = (event) => {
    setNotificationsRowsPerPage(parseInt(event.target.value, 10));
    setNotificationsPage(0);
  };

  // Navigation handler
  const gotoUnitPage = (id) => {
    navigate(`../device/${id}/engine-data`);
  };

  const handleAlarmExportMenuOpen = (event) => {
    setAlarmExportMenuAnchor(event.currentTarget);
  };

  const handleNotificationExportMenuOpen = (event) => {
    setNotificationsExportMenuAnchor(event.currentTarget);
  };

  const handleFaultClear = () => {
    fetchClearLogs();
    setNotificationFilter("faultClear");
    setNotificationsPage(0);
  };

  // Filter for Critical Alarm
  const handleCriticalAlarm = () => {
    fetchAlarmLogs();
    setNotificationFilter("criticalAlarm");
    setNotificationsPage(0);
  };

  // Get paginated data

  const sortedAlarms = useMemo(() => {
    if (!filteredAlarms.length) return [];

    const { key, direction } = alarmsSortConfig;
    const dir = direction === "asc" ? 1 : -1;

    return [...filteredAlarms].sort((a, b) => {
      if (key === "time") {
        //  return (a.time - b.time) * dir; // ✅ DESC works
        return (new Date(a.time).getTime() - new Date(b.time).getTime()) * dir; // ✅ DESC works
      }
      return 0;
    });
  }, [filteredAlarms, alarmsSortConfig]);

  const paginatedAlarms = sortedAlarms.slice(
    alarmsPage * alarmsRowsPerPage,
    alarmsPage * alarmsRowsPerPage + alarmsRowsPerPage,
  );

  const paginatedNotifications = filteredNotifications.slice(
    notificationsPage * notificationsRowsPerPage,
    notificationsPage * notificationsRowsPerPage + notificationsRowsPerPage,
  );

  return (
    <Box
      sx={{
        flexGrow: 1,
        p: { xs: 1, sm: 1 },
        height: "calc(100vh - 7vh)",
        display: "flex",
        flexDirection: "column",
        overflow: "auto",
        maxWidth: "100%",
      }}
    >
      <Grid
        container
        spacing={1}
        sx={{
          height: { xs: "auto", md: "100%" },
          flexGrow: 1,
        }}
      >
        {/* Current Alarms Table - Left Side */}
        <Grid
          item
          xs={12}
          sm={7}
          sx={{
            height: { xs: "auto", md: "100%" },
            minHeight: { xs: "400px", md: "auto" },
            display: "flex",
            width: {
              xs: "100%", // stack on mobile
              sm: "55%", // side by side from sm+
            },
          }}
        >
          <Paper
            elevation={4}
            sx={{
              p: 1.5,
              display: "flex",
              flexDirection: "column",
              height: "100%",
              width: "100%",
              position: "relative",
            }}
          >
            {/* Loading Overlay for Alarms */}
            {loadingAlarms && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  bgcolor: "rgba(187, 183, 183, 0.12)",
                  backdropFilter: "blur(10px)",
                  zIndex: 10,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <CircularProgress color="#60a5fa" size={70} />
              </Box>
            )}

            {/* Header */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  variant="h6"
                  fontSize={{ xs: "1.05rem", sm: "2rem", md: "2rem" }}
                  fontWeight="bold"
                >
                  Current Alarms: List, Search & Link to units
                </Typography>
              </Box>

              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Button
                  sx={{ borderRadius: 0 }}
                  variant="outlined"
                  color="white"
                  startIcon={<GetAppIcon />}
                  onClick={handleAlarmExportMenuOpen}
                >
                  Export
                </Button>
              </Box>
            </Box>

            {/* Search Input */}
            <TextField
              label="Search:"
              variant="outlined"
              size="small"
              value={alarmsQuery}
              onChange={handleAlarmsSearch}
              fullWidth
              sx={{
                mb: 2,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 0, // ✅ remove rounding
                },
              }}
            />

            {/* Alarms Table */}
            <TableContainer
              sx={{
                flexGrow: 1,
                maxHeight: { xs: "300px", md: "calc(100vh - 300px)" },
                overflow: "auto",
                WebkitOverflowScrolling: "touch",
                overscrollBehaviorY: "none",
              }}
            >
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: theme.palette.grey[100] }}>
                    <TableCell
                      sx={{
                        display: { xs: "table-cell", md: "none" },
                        fontWeight: "bold",
                        textAlign: "center",
                        fontSize: "12px",
                        padding: "12px 8px",
                        width: "50px",
                        color: "white",
                        backgroundColor: "#000",
                      }}
                    >
                      Details
                    </TableCell>
                    <TableCell
                      sx={{
                        textAlign: "center",
                        fontWeight: "bold",
                        color: "white",
                        backgroundColor: "#000",
                        fontSize: { xs: "11px", sm: "14px", md: "15px" },
                        padding: "8px",
                      }}
                      sortDirection={
                        alarmsSortConfig.key === "time"
                          ? alarmsSortConfig.direction
                          : false
                      }
                    >
                      <TableSortLabel
                        active={alarmsSortConfig.key === "time"}
                        direction={
                          alarmsSortConfig.key === "time"
                            ? alarmsSortConfig.direction
                            : "asc"
                        }
                        onClick={() => handleAlarmsSort("time")}
                        sx={{
                          color: "white",
                          "& .MuiTableSortLabel-icon": {
                            color: "white !important",
                          },
                        }}
                      >
                        Alarm Times
                      </TableSortLabel>
                    </TableCell>
                    <TableCell
                      sx={{
                        textAlign: "center",
                        fontWeight: "bold",
                        fontSize: { xs: "11px", sm: "14px", md: "15px" },
                        color: "white",
                        backgroundColor: "#000",
                        padding: "8px",
                      }}
                    >
                      Unit Name
                    </TableCell>
                    <TableCell
                      sx={{
                        display: { xs: "none", md: "table-cell" },
                        fontWeight: "bold",
                        fontSize: { xs: "11px", sm: "14px", md: "15px" },
                        color: "white",
                        backgroundColor: "#000",
                        padding: "8px",
                      }}
                    >
                      Modem Serial Number
                    </TableCell>
                    <TableCell
                      sx={{
                        display: { xs: "none", md: "table-cell" },
                        fontWeight: "bold",
                        fontSize: { xs: "11px", sm: "14px", md: "15px" },
                        color: "white",
                        backgroundColor: "#000",
                        padding: "8px",
                      }}
                    >
                      Region/Technician
                    </TableCell>
                    <TableCell
                      sx={{
                        display: { xs: "none", md: "table-cell" },
                        fontWeight: "bold",
                        fontSize: { xs: "11px", sm: "14px", md: "15px" },
                        color: "white",
                        backgroundColor: "#000",
                        padding: "8px",
                      }}
                    >
                      Shutdown Enum
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        fontSize: { xs: "11px", sm: "14px", md: "15px" },
                        color: "white",
                        backgroundColor: "#000",
                        textAlign: "center",
                        padding: "8px",
                      }}
                    >
                      Unit Page
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedAlarms.length > 0 ? (
                    paginatedAlarms.map((alarm, index) => (
                      <React.Fragment key={alarm.id}>
                        <TableRow
                          hover
                          sx={{
                            backgroundColor:
                              index % 2 === 0 ? "#2f3136" : "#3a3c43",
                            "&:hover": { backgroundColor: "#4a4d54" },
                          }}
                        >
                          <TableCell
                            sx={{
                              display: { xs: "table-cell", md: "none" },
                              textAlign: "center",
                            }}
                          >
                            <IconButton
                              size="small"
                              onClick={() => toggleAlarmsRowExpansion(alarm.id)}
                              sx={{ color: "white" }}
                            >
                              {expandedAlarmsRows[alarm.id] ? (
                                <ExpandLessIcon />
                              ) : (
                                <ExpandMoreIcon />
                              )}
                            </IconButton>
                          </TableCell>
                          <TableCell
                            sx={{
                              fontSize: { xs: "11px", sm: "14px", md: "15px" },
                            }}
                          >
                            {formatReadableDate(alarm.time) || "No date"}
                          </TableCell>
                          <TableCell
                            sx={{
                              fontSize: { xs: "11px", sm: "14px", md: "15px" },
                            }}
                          >
                            {alarm.unitName}
                          </TableCell>
                          <TableCell
                            sx={{
                              display: { xs: "none", md: "table-cell" },
                              fontSize: { xs: "11px", sm: "14px", md: "15px" },
                            }}
                          >
                            {alarm.unitId}
                          </TableCell>
                          <TableCell
                            sx={{
                              display: { xs: "none", md: "table-cell" },
                              fontSize: { xs: "11px", sm: "14px", md: "15px" },
                            }}
                          >
                            {alarm.regionTechnician}
                          </TableCell>
                          <TableCell
                            sx={{
                              display: { xs: "none", md: "table-cell" },
                              fontSize: { xs: "11px", sm: "14px", md: "15px" },
                            }}
                          >
                            {alarm.shutdown}
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: "center",
                              fontSize: { xs: "11px", sm: "14px", md: "15px" },
                            }}
                          >
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => gotoUnitPage(alarm.id)}
                              sx={{
                                borderRadius: 0, // ✅ remove rounding
                              }}
                            >
                              View
                            </Button>
                          </TableCell>
                        </TableRow>
                        <TableRow
                          sx={{ display: { xs: "table-row", md: "none" } }}
                        >
                          <TableCell
                            style={{ paddingBottom: 0, paddingTop: 0 }}
                            colSpan={4}
                            sx={{
                              backgroundColor:
                                index % 2 === 0 ? "#2f3136" : "#3a3c43",
                            }}
                          >
                            <Collapse
                              in={expandedAlarmsRows[alarm.id]}
                              timeout="auto"
                              unmountOnExit
                            >
                              <Box sx={{ py: 2, px: 1 }}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 1.5,
                                  }}
                                >
                                  <Box>
                                    <Typography
                                      variant="caption"
                                      sx={{ color: "#999", fontWeight: "bold" }}
                                    >
                                      Modem Serial Number:
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{ color: "white" }}
                                    >
                                      {alarm.unitId}
                                    </Typography>
                                  </Box>
                                  <Box>
                                    <Typography
                                      variant="caption"
                                      sx={{ color: "#999", fontWeight: "bold" }}
                                    >
                                      Region/Technician:
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{ color: "white" }}
                                    >
                                      {alarm.regionTechnician}
                                    </Typography>
                                  </Box>
                                  <Box>
                                    <Typography
                                      variant="caption"
                                      sx={{ color: "#999", fontWeight: "bold" }}
                                    >
                                      Shutdown Enum:
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{ color: "white" }}
                                    >
                                      {alarm.shutdown}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                        No alarms found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <TablePagination
              component="div"
              count={filteredAlarms.length}
              page={alarmsPage}
              onPageChange={handleAlarmsChangePage}
              rowsPerPage={alarmsRowsPerPage}
              onRowsPerPageChange={handleAlarmsChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
              sx={{ mt: 1 }}
            />

            {/* Export Menu */}
            <Menu
              anchorEl={alarmExportMenuAnchor}
              open={Boolean(alarmExportMenuAnchor)}
              onClose={handleAlarmExportMenuClose}
            >
              <MenuItem onClick={copyToClipboard}>
                <ListItemIcon>
                  <FileCopyIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Copy</ListItemText>
              </MenuItem>
              <MenuItem onClick={exportToCSV}>
                <ListItemIcon>
                  <DescriptionIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>CSV</ListItemText>
              </MenuItem>
              <MenuItem onClick={exportToExcel}>
                <ListItemIcon>
                  <TableViewIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Excel</ListItemText>
              </MenuItem>
              <MenuItem onClick={exportToPDF}>
                <ListItemIcon>
                  <PictureAsPdfIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>PDF</ListItemText>
              </MenuItem>
            </Menu>
          </Paper>
        </Grid>
        {/* Notifications Log - Right Side */}
        <Grid
          item
          xs={12}
          sm={5}
          sx={{
            height: { xs: "auto", md: "100%" },
            minHeight: { xs: "400px", md: "auto" },
            display: "flex",
            width: {
              xs: "100%", // stack on mobile
              sm: "42%", // side by side from sm+
            },
          }}
        >
          <Paper
            elevation={4}
            sx={{
              p: 1.5,
              display: "flex",
              flexDirection: "column",
              height: "100%",
              width: "100%",
              position: "relative",
            }}
          >
            {/* Loading Overlay for Notifications */}
            {loadingNotifications && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  bgcolor: "rgba(187, 183, 183, 0.12)",
                  backdropFilter: "blur(10px)",
                  zIndex: 10,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <CircularProgress color="#60a5fa" size={70} />
              </Box>
            )}

            {/* Header */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="h6" fontWeight="bold">
                  Notifications Log
                </Typography>
              </Box>

              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Tooltip title="Critical Alarm">
                  <IconButton
                    size="small"
                    onClick={handleCriticalAlarm}
                    sx={{
                      color:
                        notificationFilter === "criticalAlarm"
                          ? "error.main"
                          : "text.secondary",
                      transition: "0.2s",
                      "&:hover": { color: "error.main" },
                    }}
                  >
                    <NotificationsActiveIcon />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Fault Clear">
                  <IconButton
                    size="small"
                    onClick={handleFaultClear}
                    sx={{
                      color:
                        notificationFilter === "faultClear"
                          ? "primary.main"
                          : "text.secondary",
                      transition: "0.2s",
                      "&:hover": { color: "primary.main" },
                    }}
                  >
                    <ClearAllIcon />
                  </IconButton>
                </Tooltip>

                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <Button
                    sx={{ borderRadius: 0 }}
                    variant="outlined"
                    color="white"
                    startIcon={<GetAppIcon />}
                    onClick={handleNotificationExportMenuOpen}
                  >
                    Export
                  </Button>
                </Box>
              </Box>
            </Box>

            {/* Search Input */}
            <TextField
              label="Search:"
              variant="outlined"
              size="small"
              value={notificationsQuery}
              onChange={handleNotificationsSearch}
              fullWidth
              sx={{
                mb: 2,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 0, // ✅ remove rounding
                },
              }}
            />

            {/* Notifications Table */}
            <TableContainer
              sx={{
                flexGrow: 1,
                maxHeight: { xs: "300px", md: "calc(100vh - 300px)" },
                overflow: "auto",
                WebkitOverflowScrolling: "touch",
                overscrollBehaviorY: "none",
              }}
            >
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#000" }}>
                    {/* Mobile expand button column */}
                    <TableCell
                      sx={{
                        display: { xs: "table-cell", md: "none" },
                        fontWeight: "bold",
                        textAlign: "center",
                        fontSize: "12px",
                        padding: "12px 8px",
                        width: "50px",
                        color: "white",
                        backgroundColor: "#000",
                      }}
                    >
                      Details
                    </TableCell>

                    <TableCell
                      sx={{
                        textAlign: "center",
                        fontWeight: "bold",
                        color: "white",
                        backgroundColor: "#000",
                        fontSize: { xs: "11px", sm: "14px", md: "15px" },
                        padding: "8px",
                      }}
                      sortDirection={
                        notificationsSortConfig.key === "time"
                          ? notificationsSortConfig.direction
                          : false
                      }
                    >
                      <TableSortLabel
                        active={notificationsSortConfig.key === "time"}
                        direction={
                          notificationsSortConfig.key === "time"
                            ? notificationsSortConfig.direction
                            : "asc"
                        }
                        onClick={() => handleNotificationsSort("time")}
                        sx={{
                          color: "white",
                          "& .MuiTableSortLabel-icon": {
                            color: "white !important",
                          },
                        }}
                      >
                        Time
                      </TableSortLabel>
                    </TableCell>

                    <TableCell
                      sx={{
                        display: { xs: "none", md: "table-cell" },
                        fontWeight: "bold",
                        fontSize: { xs: "11px", sm: "14px", md: "15px" },
                        color: "white",
                        backgroundColor: "#000",
                        padding: "8px",
                      }}
                    >
                      Notification
                    </TableCell>

                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        fontSize: { xs: "11px", sm: "14px", md: "15px" },
                        color: "white",
                        backgroundColor: "#000",
                        textAlign: "left",
                        padding: "8px",
                      }}
                    >
                      Recipients
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedNotifications.length > 0 ? (
                    paginatedNotifications.map((notif, index) => (
                      <React.Fragment key={notif.id}>
                        <TableRow
                          hover
                          sx={{
                            backgroundColor:
                              index % 2 === 0 ? "#2f3136" : "#3a3c43",
                            "&:hover": {
                              backgroundColor: "#4a4d54",
                            },
                          }}
                        >
                          {/* Expand Button - Mobile Only */}
                          <TableCell
                            sx={{
                              display: { xs: "table-cell", md: "none" },
                              textAlign: "center",
                            }}
                          >
                            <IconButton
                              size="small"
                              onClick={() =>
                                toggleNotificationsRowExpansion(notif.id)
                              }
                              sx={{ color: "white" }}
                            >
                              {expandedNotificationsRows[notif.id] ? (
                                <ExpandLessIcon />
                              ) : (
                                <ExpandMoreIcon />
                              )}
                            </IconButton>
                          </TableCell>

                          <TableCell
                            sx={{
                              fontSize: { xs: "11px", sm: "14px", md: "15px" },
                              textAlign: "center",
                            }}
                          >
                            {formatReadableDate(notif.time) || "No date"}
                          </TableCell>

                          <TableCell
                            sx={{
                              display: { xs: "none", md: "table-cell" },
                              fontSize: { xs: "11px", sm: "14px", md: "15px" },
                            }}
                          >
                            {notif.notification}
                          </TableCell>

                          <TableCell
                            sx={{
                              fontSize: { xs: "11px", sm: "14px", md: "15px" },
                            }}
                          >
                            <Tooltip title="Open form">
                              <Box
                                onClick={
                                  notif.recipientsLength + notif.emailsLength >
                                    0
                                    ? () => OpenInfo(notif)
                                    : undefined
                                }
                                sx={{
                                  borderRadius: 0,
                                  width: 34,
                                  height: 34,
                                  backgroundColor:
                                    notif.recipientsLength +
                                      notif.emailsLength >
                                      0
                                      ? "#bd0d0dff"
                                      : "grey.700",
                                  cursor:
                                    notif.recipientsLength +
                                      notif.emailsLength >
                                      0
                                      ? "pointer"
                                      : "not-allowed",
                                  transition: "all 0.2s ease",
                                  color: "white",
                                  fontSize: 13,
                                  fontWeight: 600,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  "&:hover":
                                    notif.recipientsLength +
                                      notif.emailsLength >
                                      0
                                      ? {
                                        backgroundColor: "primary.main",
                                        transform: "scale(1.1)",
                                      }
                                      : {},
                                }}
                              >
                                {notif.recipientsLength}/{notif.emailsLength}
                              </Box>
                            </Tooltip>
                          </TableCell>
                        </TableRow>

                        {/* Mobile Collapsed Row */}
                        <TableRow
                          sx={{ display: { xs: "table-row", md: "none" } }}
                        >
                          <TableCell
                            style={{ paddingBottom: 0, paddingTop: 0 }}
                            colSpan={3}
                            sx={{
                              backgroundColor:
                                index % 2 === 0 ? "#2f3136" : "#3a3c43",
                            }}
                          >
                            <Collapse
                              in={expandedNotificationsRows[notif.id]}
                              timeout="auto"
                              unmountOnExit
                            >
                              <Box sx={{ py: 2, px: 1 }}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 1.5,
                                  }}
                                >
                                  <Box>
                                    <Typography
                                      variant="caption"
                                      sx={{ color: "#999", fontWeight: "bold" }}
                                    >
                                      Notification:
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{ color: "white" }}
                                    >
                                      {notif.notification}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                        No data available in table
                        <br />
                        <Typography variant="caption" color="text.secondary">
                          Showing 0 to 0 of 0 entries
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            {filteredNotifications.length > 0 && (
              <TablePagination
                component="div"
                count={filteredNotifications.length}
                page={notificationsPage}
                onPageChange={handleNotificationsChangePage}
                rowsPerPage={notificationsRowsPerPage}
                onRowsPerPageChange={handleNotificationsChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
                sx={{ mt: 1 }}
              />
            )}

            {/* Export Menu */}
            <Menu
              anchorEl={notificationsExportMenuAnchor}
              open={Boolean(notificationsExportMenuAnchor)}
              onClose={handleNotificationsExportMenuClose}
            >
              <MenuItem onClick={copyNotificationsToClipboard}>
                <ListItemIcon>
                  <FileCopyIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Copy</ListItemText>
              </MenuItem>
              <MenuItem onClick={exportNotificationsToCSV}>
                <ListItemIcon>
                  <DescriptionIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>CSV</ListItemText>
              </MenuItem>
              <MenuItem onClick={exportNotificationsToExcel}>
                <ListItemIcon>
                  <TableViewIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Excel</ListItemText>
              </MenuItem>
              <MenuItem onClick={exportNotificationsToPDF}>
                <ListItemIcon>
                  <PictureAsPdfIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>PDF</ListItemText>
              </MenuItem>
            </Menu>
          </Paper>

          <InfoNotification
            NotificationData={selectedNotification}
            open={showInformationNotification}
            onClose={() => setShowInformationNotification(false)}
          />
        </Grid>
      </Grid>
    </Box>
  );
}
